/* Everyday Creators — GSAP entrance, float, parallax (desktop) + marquee (mobile).
   Progressive enhancement: without GSAP everything stays visible; the tablet
   strip falls back to native horizontal scroll. */
(function () {
  'use strict';

  var SELECTOR = '.ecw';
  var DESKTOP = '(min-width: 990px)';
  var TABLET = '(min-width: 750px) and (max-width: 989px)';
  var PHONE = '(max-width: 749px)';

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function waitForGsap(cb) {
    var tries = 0;
    (function check() {
      if (window.gsap) {
        cb(window.gsap, window.ScrollTrigger || null);
        return;
      }
      if (tries++ > 60) {
        cb(null, null);
        return;
      }
      window.setTimeout(check, 50);
    })();
  }

  function EcwSection(root, gsap, ScrollTrigger) {
    this.root = root;
    this.gsap = gsap;
    this.ST = ScrollTrigger;
    this.stage = root.querySelector('[data-ecw-stage]');
    this.viewport = root.querySelector('[data-ecw-viewport]');
    this.track = root.querySelector('[data-ecw-track]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-ecw-card]'));
    this.reveals = Array.prototype.slice.call(root.querySelectorAll('[data-ecw-reveal]'));
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.enableAnim = root.getAttribute('data-ecw-animate') === 'true';
    this.enableFloat = root.getAttribute('data-ecw-float') === 'true';
    this.enableParallax = root.getAttribute('data-ecw-parallax') === 'true';
    this.mm = null;
    this.init();
  }

  EcwSection.prototype.init = function () {
    var self = this;
    var gsap = this.gsap;
    if (!this.stage || !this.track || !this.cards.length) return;

    // No GSAP (or too old to support matchMedia): leave the CSS default state.
    if (!gsap || typeof gsap.matchMedia !== 'function') return;
    if (this.ST) gsap.registerPlugin(this.ST);

    this.mm = gsap.matchMedia();
    this.mm.add(DESKTOP, function () {
      return self.setupDesktop();
    });
    this.mm.add(TABLET, function () {
      return self.setupMobile();
    });
    this.mm.add(PHONE, function () {
      return self.setupPhone();
    });
  };

  EcwSection.prototype.setupDesktop = function () {
    var self = this;
    var gsap = this.gsap;
    var ST = this.ST;
    var reduced = this.reduced;
    var inners = this.cards.map(function (c) {
      return c.querySelector('[data-ecw-inner]');
    });

    var tl = null;
    var floats = [];
    var parallax = [];
    var hovers = [];
    var onMove = null;
    var onLeave = null;
    var pointerOK = false;

    function startFloat() {
      if (!self.enableFloat || reduced) return;
      inners.forEach(function (el) {
        floats.push(
          gsap.to(el, {
            y: '+=' + gsap.utils.random(-12, 12, 1),
            duration: gsap.utils.random(3, 5, 0.1),
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
            delay: gsap.utils.random(0, 1.4)
          })
        );
      });
    }

    // Entrance
    if (this.enableAnim && !reduced) {
      gsap.set(inners, { autoAlpha: 0, scale: 0.82, y: 42 });
      gsap.set(this.reveals, { autoAlpha: 0, y: 26 });
      tl = gsap.timeline({
        scrollTrigger: { trigger: this.stage, start: 'top 78%', once: true },
        onComplete: startFloat
      });
      tl.to(inners, {
        autoAlpha: 1,
        scale: 1,
        y: 0,
        duration: 0.72,
        ease: 'back.out(1.4)',
        stagger: { each: 0.05, from: 'random' }
      }).to(
        this.reveals,
        { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.12 },
        '-=0.35'
      );
    } else {
      gsap.set(inners, { autoAlpha: 1, scale: 1, y: 0 });
      gsap.set(this.reveals, { autoAlpha: 1, y: 0 });
      startFloat();
    }

    // Scroll parallax (outer card element only)
    if (this.enableParallax && !reduced && ST) {
      this.cards.forEach(function (card) {
        var depth = gsap.utils.random(-20, 20, 1);
        parallax.push(
          gsap.fromTo(
            card,
            { y: -depth },
            {
              y: depth,
              ease: 'none',
              scrollTrigger: { trigger: self.stage, start: 'top bottom', end: 'bottom top', scrub: true }
            }
          )
        );
      });
    }

    // Pointer parallax on the whole track (desktop pointers only)
    pointerOK = window.matchMedia('(hover: hover) and (pointer: fine)').matches && !reduced;
    if (pointerOK) {
      var qx = gsap.quickTo(this.track, 'x', { duration: 0.6, ease: 'power3' });
      var qy = gsap.quickTo(this.track, 'y', { duration: 0.6, ease: 'power3' });
      onMove = function (e) {
        var r = self.stage.getBoundingClientRect();
        qx(((e.clientX - r.left) / r.width - 0.5) * 18);
        qy(((e.clientY - r.top) / r.height - 0.5) * 12);
      };
      onLeave = function () {
        qx(0);
        qy(0);
      };
      this.stage.addEventListener('mousemove', onMove);
      this.stage.addEventListener('mouseleave', onLeave);
    }

    // Hover lift (scale is independent of the float y)
    if (!reduced) {
      inners.forEach(function (el) {
        var card = el.parentNode;
        function enter() {
          card.classList.add('is-hover');
          gsap.to(el, { scale: 1.06, duration: 0.32, ease: 'power2.out', overwrite: 'auto' });
        }
        function leave() {
          card.classList.remove('is-hover');
          gsap.to(el, { scale: 1, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
        }
        card.addEventListener('mouseenter', enter);
        card.addEventListener('mouseleave', leave);
        hovers.push([card, enter, leave]);
      });
    }

    if (ST) ST.refresh();

    return function cleanup() {
      if (tl) {
        if (tl.scrollTrigger) tl.scrollTrigger.kill();
        tl.kill();
      }
      floats.forEach(function (t) {
        t.kill();
      });
      parallax.forEach(function (t) {
        if (t.scrollTrigger) t.scrollTrigger.kill();
        t.kill();
      });
      if (pointerOK) {
        self.stage.removeEventListener('mousemove', onMove);
        self.stage.removeEventListener('mouseleave', onLeave);
      }
      hovers.forEach(function (h) {
        h[0].removeEventListener('mouseenter', h[1]);
        h[0].removeEventListener('mouseleave', h[2]);
        h[0].classList.remove('is-hover');
      });
      gsap.set(self.track, { clearProps: 'transform' });
      gsap.set(self.cards, { clearProps: 'transform' });
      gsap.set(inners, { clearProps: 'all' });
      gsap.set(self.reveals, { clearProps: 'all' });
    };
  };

  EcwSection.prototype.setupMobile = function () {
    var self = this;
    var gsap = this.gsap;
    var reduced = this.reduced;
    var marquee = null;
    var clones = [];
    var revealTween = null;

    if (this.enableAnim && !reduced) {
      gsap.set(this.reveals, { autoAlpha: 0, y: 20 });
      revealTween = gsap.to(this.reveals, {
        autoAlpha: 1,
        y: 0,
        duration: 0.6,
        ease: 'power3.out',
        stagger: 0.1,
        scrollTrigger: { trigger: this.stage, start: 'top 85%', once: true }
      });
    }

    // Seamless auto-marquee (clone set is identical so the loop has no seam)
    if (!reduced && this.cards.length > 1) {
      this.cards.forEach(function (c) {
        var clone = c.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-ecw-card');
        clone.classList.add('ecw__card--clone');
        self.track.appendChild(clone);
        clones.push(clone);
      });
      this.viewport.style.overflowX = 'hidden';
      // Exact distance from the first original to its first clone = seamless loop.
      var dist = clones[0].offsetLeft - this.cards[0].offsetLeft;
      if (dist > 0) {
        marquee = gsap.to(this.track, {
          x: -dist,
          duration: Math.max(16, dist / 60),
          ease: 'none',
          repeat: -1
        });
      }
    }

    return function cleanup() {
      if (revealTween) {
        if (revealTween.scrollTrigger) revealTween.scrollTrigger.kill();
        revealTween.kill();
      }
      if (marquee) marquee.kill();
      clones.forEach(function (c) {
        c.remove();
      });
      gsap.set(self.track, { clearProps: 'transform' });
      self.viewport.style.overflowX = '';
      gsap.set(self.reveals, { clearProps: 'all' });
    };
  };

  EcwSection.prototype.setupPhone = function () {
    var self = this;
    var gsap = this.gsap;
    var reduced = this.reduced;
    var revealTween = null;

    if (this.enableAnim && !reduced) {
      gsap.set(this.reveals, { autoAlpha: 0, y: 20 });
      revealTween = gsap.to(this.reveals, {
        autoAlpha: 1,
        y: 0,
        duration: 0.6,
        ease: 'power3.out',
        stagger: 0.1,
        scrollTrigger: { trigger: this.stage, start: 'top 85%', once: true }
      });
    }

    return function cleanup() {
      if (revealTween) {
        if (revealTween.scrollTrigger) revealTween.scrollTrigger.kill();
        revealTween.kill();
      }
      gsap.set(self.track, { clearProps: 'transform' });
      gsap.set(self.reveals, { clearProps: 'all' });
    };
  };

  EcwSection.prototype.destroy = function () {
    if (this.mm) {
      this.mm.revert();
      this.mm = null;
    }
    this.root.__ecw = null;
  };

  function initRoot(root, gsap, ST) {
    if (!root || root.__ecw) return;
    root.__ecw = new EcwSection(root, gsap, ST);
  }

  function findRoot(el) {
    if (!el) return null;
    if (el.matches && el.matches(SELECTOR)) return el;
    return el.querySelector ? el.querySelector(SELECTOR) : null;
  }

  ready(function () {
    waitForGsap(function (gsap, ST) {
      document.querySelectorAll(SELECTOR).forEach(function (root) {
        initRoot(root, gsap, ST);
      });
    });
  });

  document.addEventListener('shopify:section:load', function (e) {
    var root = findRoot(e.target);
    if (!root) return;
    waitForGsap(function (gsap, ST) {
      initRoot(root, gsap, ST);
    });
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = findRoot(e.target);
    if (root && root.__ecw) root.__ecw.destroy();
  });
})();
